import { useState, useEffect, useMemo } from 'react';
import { Search, Filter, X, Clock, Users, FileText, Calendar, Shield } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useUserRoles } from '@/hooks/useUserRoles';
import { cn } from '@/lib/utils';

interface SearchResult {
  id: string;
  title: string;
  description: string;
  type: 'guard' | 'team' | 'report' | 'schedule' | 'mission' | 'client';
  url: string;
  metadata?: Record<string, any>;
  score?: number;
}

interface SearchFilters {
  types: string[];
  dateRange: string;
  teams: string[];
}

const searchTypes = [
  { key: 'guard', label: 'Guards', icon: Shield },
  { key: 'team', label: 'Teams', icon: Users },
  { key: 'report', label: 'Reports', icon: FileText },
  { key: 'schedule', label: 'Schedules', icon: Calendar },
  { key: 'mission', label: 'Missions', icon: FileText },
  { key: 'client', label: 'Clients', icon: Users },
];

export const GlobalSearch = () => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState<SearchFilters>({
    types: [],
    dateRange: 'all',
    teams: []
  });
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  
  const navigate = useNavigate();
  const { isAdmin } = useUserRoles();

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('shiftask-recent-searches');
    if (saved) {
      setRecentSearches(JSON.parse(saved));
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (!query.trim() || query.length < 2) {
      setResults([]);
      return;
    }

    const timer = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => clearTimeout(timer);
  }, [query, filters]);

  const performSearch = async (searchQuery: string) => {
    setIsLoading(true);
    try {
      const searchResults: SearchResult[] = [];
      const lowerQuery = searchQuery.toLowerCase();

      // Search guards
      if (shouldSearchType('guard')) {
        const { data: guards } = await supabase
          .from('profiles')
          .select('id, full_name, first_name, last_name, email')
          .or(`full_name.ilike.%${searchQuery}%,first_name.ilike.%${searchQuery}%,last_name.ilike.%${searchQuery}%,email.ilike.%${searchQuery}%`)
          .limit(5);

        guards?.forEach(guard => {
          searchResults.push({
            id: guard.id,
            title: guard.full_name || `${guard.first_name} ${guard.last_name}`,
            description: guard.email,
            type: 'guard',
            url: `/guards?search=${guard.id}`,
            metadata: { email: guard.email }
          });
        });
      }

      // Search teams
      if (shouldSearchType('team')) {
        const { data: teams } = await supabase
          .from('teams')
          .select('id, name, description')
          .or(`name.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`)
          .limit(5);

        teams?.forEach(team => {
          searchResults.push({
            id: team.id,
            title: team.name,
            description: team.description || 'Team',
            type: 'team',
            url: `/teams/${team.id}`,
            metadata: { description: team.description }
          });
        });
      }

      // Search reports
      if (shouldSearchType('report')) {
        const { data: reports } = await supabase
          .from('reports')
          .select('id, title, content, submitted_at')
          .or(`title.ilike.%${searchQuery}%,content.ilike.%${searchQuery}%`)
          .limit(5);

        reports?.forEach(report => {
          searchResults.push({
            id: report.id,
            title: report.title,
            description: report.content || 'Report',
            type: 'report',
            url: `/reports`,
            metadata: { submitted_at: report.submitted_at }
          });
        });
      }

      // Search missions
      if (shouldSearchType('mission')) {
        const { data: missions } = await supabase
          .from('missions')
          .select('id, title, description, team_id')
          .or(`title.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`)
          .limit(5);

        missions?.forEach(mission => {
          searchResults.push({
            id: mission.id,
            title: mission.title,
            description: mission.description || 'Mission',
            type: 'mission',
            url: `/missions/${mission.team_id}`,
            metadata: { team_id: mission.team_id }
          });
        });
      }

      // Search clients (admin only)
      if (isAdmin && shouldSearchType('client')) {
        const { data: clients } = await supabase
          .from('profiles')
          .select('id, full_name, first_name, last_name, email')
          .or(`full_name.ilike.%${searchQuery}%,first_name.ilike.%${searchQuery}%,last_name.ilike.%${searchQuery}%,email.ilike.%${searchQuery}%`)
          .limit(5);

        clients?.forEach(client => {
          searchResults.push({
            id: client.id,
            title: client.full_name || `${client.first_name} ${client.last_name}`,
            description: client.email,
            type: 'client',
            url: `/clients?search=${client.id}`,
            metadata: { email: client.email }
          });
        });
      }

      setResults(searchResults);
      saveRecentSearch(searchQuery);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const shouldSearchType = (type: string) => {
    return filters.types.length === 0 || filters.types.includes(type);
  };

  const saveRecentSearch = (searchQuery: string) => {
    const updated = [searchQuery, ...recentSearches.filter(s => s !== searchQuery)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('shiftask-recent-searches', JSON.stringify(updated));
  };

  const handleResultClick = (result: SearchResult) => {
    navigate(result.url);
    setIsOpen(false);
    setQuery('');
  };

  const toggleFilter = (type: string) => {
    setFilters(prev => ({
      ...prev,
      types: prev.types.includes(type)
        ? prev.types.filter(t => t !== type)
        : [...prev.types, type]
    }));
  };

  const clearFilters = () => {
    setFilters({
      types: [],
      dateRange: 'all',
      teams: []
    });
  };

  const getTypeIcon = (type: string) => {
    const typeConfig = searchTypes.find(t => t.key === type);
    return typeConfig?.icon || Search;
  };

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
        <Input
          placeholder="Search guards, teams, reports..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsOpen(true)}
          className="pl-10 pr-10"
        />
        {query && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setQuery('')}
            className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
          >
            <X className="w-3 h-3" />
          </Button>
        )}
      </div>

      {isOpen && (
        <Card className="absolute top-full left-0 right-0 mt-2 z-50 max-h-96 bg-background border shadow-lg">
          <div className="p-3 border-b">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-muted-foreground">Filters:</span>
              {searchTypes.map(type => {
                const Icon = type.icon;
                const isActive = filters.types.includes(type.key);
                return (
                  <Button
                    key={type.key}
                    variant={isActive ? "default" : "outline"}
                    size="sm"
                    onClick={() => toggleFilter(type.key)}
                    className="h-6 px-2 text-xs"
                  >
                    <Icon className="w-3 h-3 mr-1" />
                    {type.label}
                  </Button>
                );
              })}
              {filters.types.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="h-6 px-2 text-xs"
                >
                  Clear
                </Button>
              )}
            </div>
          </div>

          <ScrollArea className="max-h-64">
            {query.length < 2 ? (
              <div className="p-4">
                {recentSearches.length > 0 ? (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium text-muted-foreground">Recent Searches</span>
                    </div>
                    {recentSearches.map((search, index) => (
                      <Button
                        key={index}
                        variant="ghost"
                        size="sm"
                        onClick={() => setQuery(search)}
                        className="w-full justify-start h-8 mb-1"
                      >
                        {search}
                      </Button>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center">
                    Type at least 2 characters to search
                  </p>
                )}
              </div>
            ) : isLoading ? (
              <div className="p-4 text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent mx-auto"></div>
                <p className="text-sm text-muted-foreground mt-2">Searching...</p>
              </div>
            ) : results.length > 0 ? (
              <div className="p-2">
                {results.map((result, index) => {
                  const Icon = getTypeIcon(result.type);
                  return (
                    <div
                      key={`${result.type}-${result.id}-${index}`}
                      onClick={() => handleResultClick(result)}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted cursor-pointer transition-colors"
                    >
                      <div className="flex-shrink-0">
                        <Icon className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium truncate">{result.title}</h4>
                        <p className="text-xs text-muted-foreground truncate">{result.description}</p>
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {searchTypes.find(t => t.key === result.type)?.label || result.type}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-4 text-center">
                <p className="text-sm text-muted-foreground">No results found for "{query}"</p>
              </div>
            )}
          </ScrollArea>
        </Card>
      )}

      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};